import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "configureStore";
import {
  Layout,
  Card,
  Text,
  BlockStack,
  TextContainer,
  Button,
  InlineStack,
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import {
  getAuthCodeUrl,
  disconnectMicrosoftSync,
} from "services/user-select.service";
import {
  setModalxComponent,
  setModalxComponentProps,
  setIsModalxOpen,
} from "components/modalx/modalx.slice";
import {
  deleteUserAvatar,
  fetchAvailableAvatars,
  importAvatarFromCompany,
  getUserAvatarUrl,
  AvailableAvatar,
} from "services/user-avatar.service";
import UserAvatar from "components/user-avatar/user-avatar";
import { fetchOnlineThunk } from "features/presence/presence.slice";
import { toast } from "sonner";
import { OwnBirthdayCard } from "containers/user-profile/own-birthday-card";
import PermissionChips from "components/permission-picker/PermissionChips";
import CabinetWork from "features/cabinet-work/cabinet-work";
import GuestCompanyBanner from "containers/user-profile/guest-company-banner";
import { User } from "types/User";

interface ProfileInfoTabProps {
  companyId: string;
  userId: string;
  userEntity: User;
  isGuest: boolean;
  /** Fetches the person again (after a photo change or an Outlook disconnect). */
  reloadUser: () => void;
}

/** Work, avatar, personal details, birthday and Outlook connection of the person. */
export default function ProfileInfoTab({
  companyId,
  userId,
  userEntity,
  isGuest,
  reloadUser,
}: ProfileInfoTabProps) {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();

  const handleOpenAvatarUpload = () => {
    dispatch(setModalxComponent("AvatarUploadModal"));
    dispatch(setModalxComponentProps({ companyId, userId }));
    dispatch(setIsModalxOpen(true));
  };

  const handleDeleteAvatar = async () => {
    try {
      await deleteUserAvatar(companyId, userId);
      toast.success(t("avatar_removed"));
      reloadUser();
      dispatch(fetchOnlineThunk(companyId));
    } catch (err: any) {
      toast.error(err?.response?.data?.error || t("error"));
    }
  };

  const [availableAvatars, setAvailableAvatars] = useState<AvailableAvatar[]>([]);
  const [importingFrom, setImportingFrom] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await fetchAvailableAvatars(companyId, userId);
        if (!cancelled) setAvailableAvatars(list);
      } catch {
        if (!cancelled) setAvailableAvatars([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, userId, userEntity.avatarFileId]);

  const handleImportAvatar = async (fromCompanyId: string) => {
    setImportingFrom(fromCompanyId);
    try {
      await importAvatarFromCompany(companyId, userId, fromCompanyId);
      toast.success(t("avatar_imported"));
      reloadUser();
      dispatch(fetchOnlineThunk(companyId));
    } catch (err: any) {
      toast.error(err?.response?.data?.error || t("avatar_import_failed"));
    } finally {
      setImportingFrom(null);
    }
  };

  const handleAuthCodeUrl = async () => {
    const authCodeUrl = await getAuthCodeUrl(companyId);
    // Redirect the user to the generated URL
    window.location.href = authCodeUrl;
  };

  const handleDisconnect = async () => {
    await disconnectMicrosoftSync(companyId);
    reloadUser();
  };

  return (
    <Layout>
      <Layout.Section>
        <BlockStack gap="500">
          {isGuest && <GuestCompanyBanner companyId={companyId} />}
          {/* A guest member has no employment here, so there is no «Darbas» to show. */}
          {!isGuest && <CabinetWork />}
          <Card roundedAbove="sm">
            <div className="flex items-center gap-4">
              <UserAvatar
                companyId={companyId}
                user={{
                  id: userEntity.id,
                  name: userEntity.name || userEntity.email,
                  avatarFileId: userEntity.avatarFileId,
                }}
                size={96}
                rounded="lg"
              />
              <div className="flex flex-col gap-2">
                <Button onClick={handleOpenAvatarUpload}>
                  {userEntity.avatarFileId ? t("change_photo") : t("upload_photo")}
                </Button>
                {userEntity.avatarFileId && (
                  <Button onClick={handleDeleteAvatar} tone="critical" variant="plain">
                    {t("remove_photo")}
                  </Button>
                )}
              </div>
            </div>
            {availableAvatars.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <Text as="p" variant="bodyMd">
                  <strong>{t("use_photo_from_other_company")}</strong>
                </Text>
                <div className="flex flex-wrap gap-3 mt-3">
                  {availableAvatars.map((a) => (
                    <div
                      key={a.companyId}
                      className="flex flex-col items-center gap-2 max-w-[120px]"
                    >
                      <img
                        src={getUserAvatarUrl(a.companyId, a.fileId)}
                        alt={a.companyName}
                        className="w-16 h-16 rounded-lg object-cover bg-gray-200"
                      />
                      <span className="text-xs text-gray-600 text-center break-words leading-tight">
                        {a.companyName}
                      </span>
                      <Button
                        size="micro"
                        loading={importingFrom === a.companyId}
                        disabled={!!importingFrom}
                        onClick={() => handleImportAvatar(a.companyId)}
                      >
                        {t("use_this_photo")}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>
          <Card roundedAbove="sm">
            <TextContainer>
              <Text as="p" variant="bodyMd">
                <strong>{t("Email")}:</strong> {userEntity.email}
              </Text>
              {userEntity.name ? (
                <Text as="p" variant="bodyMd">
                  <strong>{t("name")}:</strong> {userEntity.name}
                </Text>
              ) : (
                <Text as="p" variant="bodyMd">
                  {t("no_name_provided")}
                </Text>
              )}
              {userEntity.phone ? (
                <Text as="p" variant="bodyMd">
                  <strong>{t("phone")}:</strong> {userEntity.phone}
                </Text>
              ) : (
                <Text as="p" variant="bodyMd">
                  {t("no_phone_number_provided")}
                </Text>
              )}
              {userEntity.externalCode ? (
                <Text as="p" variant="bodyMd">
                  <strong>{t("external_code")}:</strong>{" "}
                  {userEntity.externalCode}
                </Text>
              ) : (
                <Text as="p" variant="bodyMd">
                  {t("no_external_code_provided")}
                </Text>
              )}
              {userEntity?.manager ? (
                <Text as="p" variant="bodyMd">
                  <strong>{t("manager")}:</strong>{" "}
                  {userEntity.manager.name}
                </Text>
              ) : (
                <Text as="p" variant="bodyMd">
                  {t("no_manager_provided")}
                </Text>
              )}
              {userEntity.permissions.length > 0 ? (
                <>
                  <Text as="p" variant="bodyMd">
                    <strong>{t("user_permissions")}:</strong>
                  </Text>
                  <PermissionChips permissions={userEntity.permissions} />
                </>
              ) : (
                <Text as="p" variant="bodyMd">
                  {t("no_user_permissions_provided")}
                </Text>
              )}
            </TextContainer>
          </Card>

          {!isGuest && <OwnBirthdayCard />}

          {!isGuest && (
            <Card roundedAbove="sm">
              <Text as="p" variant="bodyMd">
                <strong>{t("email_connect")}:</strong> {userEntity.email}
              </Text>

              <Text as="p" variant="bodyMd">
                <strong>{t("status")}:</strong>{" "}
                {userEntity?.emailSync?.microsoft?.isConnected
                  ? t("connected")
                  : t("not_connected")}
              </Text>
              {!userEntity?.emailSync?.microsoft?.isConnected && (
                <InlineStack align="center">
                  <Button size="large" onClick={handleAuthCodeUrl}>
                    {t("connect")}
                  </Button>
                </InlineStack>
              )}
              {userEntity?.emailSync?.microsoft?.isConnected && (
                <InlineStack align="center">
                  <Button size="large" onClick={handleDisconnect}>
                    {t("disconnect")}
                  </Button>
                </InlineStack>
              )}
            </Card>
          )}
        </BlockStack>
      </Layout.Section>
    </Layout>
  );
}
